import React, { useRef, useState, useEffect } from 'react';
import { Phone, Mail, MapPin, Clock } from 'lucide-react';

const useInView = (threshold = 0.1) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setInView(true);
    }, { threshold });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return [ref, inView];
};

const AnimatedSection = ({ children, className = '', delay = 0 }) => {
  const [ref, inView] = useInView();
  return (
    <div ref={ref} className={className} style={{
      opacity: inView ? 1 : 0,
      transform: inView ? 'translateY(0)' : 'translateY(40px)',
      transition: `opacity 0.7s ease ${delay}s, transform 0.7s ease ${delay}s`
    }}>
      {children}
    </div>
  );
};

const ContactPage = ({ onNavigate }) => {
  return (
    <div style={{ backgroundColor: '#f8f7f4', color: '#1a1a2e' }}>
      {/* Hero */}
      <section className="relative py-32" style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)' }}>
        <div className="absolute top-0 left-0 w-full h-1" style={{ background: 'linear-gradient(90deg, transparent, #C9A84C, transparent)' }}/>
        <div className="relative z-10 text-center text-white px-4">
          <p className="text-sm font-bold tracking-widest mb-4" style={{ color: '#C9A84C' }}>KONTAKT</p>
          <h1 className="text-5xl md:text-6xl font-bold mb-6">Kom i <span style={{ color: '#C9A84C' }}>Kontakt</span></h1>
          <p className="text-xl max-w-2xl mx-auto" style={{ color: 'rgba(255,255,255,0.8)' }}>Vi svarar alltid inom 24 timmar.</p>
        </div>
      </section>

      <section className="py-24 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16">
            {/* Contact Cards */}
            <div className="space-y-6">
              <AnimatedSection>
                <p className="text-sm font-bold tracking-widest mb-6" style={{ color: '#C9A84C' }}>HUR DU NÅR OSS</p>
              </AnimatedSection>
              {[
                { icon: <Phone className="w-6 h-6"/>, title: 'Telefon', info: '073-941-43-66', action: 'tel:0739414366', actionText: 'Ring Oss' },
                { icon: <Mail className="w-6 h-6"/>, title: 'Email', info: 'kontakt@galaxab.se', action: 'mailto:kontakt@galaxab.se', actionText: 'Skicka Email' },
                { icon: <MapPin className="w-6 h-6"/>, title: 'Adress', info: 'Agneshögsgatan 24, Motala 591 70', action: 'https://maps.google.com/?q=Agneshögsgatan+24+Motala', actionText: 'Öppna Maps' },
                { icon: <Clock className="w-6 h-6"/>, title: 'Öppettider', info: 'Mån-Fre: 07:00-20:00 • Lör: 08:00-18:00', action: null }
              ].map((method, idx) => (
                <AnimatedSection key={idx} delay={idx * 0.1}>
                  <div className="flex items-start space-x-4 p-6 rounded-2xl transition-all hover:scale-105"
                    style={{ backgroundColor: '#fff', border: '1px solid #e8e4d9', boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}>
                    <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#1a1a2e' }}>
                      <div style={{ color: '#C9A84C' }}>{method.icon}</div>
                    </div>
                    <div>
                      <h3 className="font-bold mb-1" style={{ color: '#1a1a2e' }}>{method.title}</h3>
                      <p className="mb-2" style={{ color: '#666' }}>{method.info}</p>
                      {method.action && (
                        <a href={method.action} target={method.title === 'Adress' ? '_blank' : undefined}
                          rel="noopener noreferrer"
                          className="text-sm font-bold transition-colors hover:opacity-70" style={{ color: '#C9A84C' }}>
                          {method.actionText} →
                        </a>
                      )}
                    </div>
                  </div>
                </AnimatedSection>
              ))}
            </div>

            {/* FAQ */}
            <AnimatedSection delay={0.2}>
              <p className="text-sm font-bold tracking-widest mb-6" style={{ color: '#C9A84C' }}>VANLIGA FRÅGOR</p>
              <div className="space-y-4">
                {[
                  { q: 'Prissättning', a: 'Från 65 kr/m². Med 50% RUT-avdrag, förutom kontorsstädning.' },
                  { q: 'Bokning', a: 'Minst 48 timmar i förväg för bästa tillgänglighet' },
                  { q: 'Betalning', a: 'Swish, kort eller faktura — du betalar efter slutförd städning' },
                  { q: 'Garanti', a: '100% nöjdhet eller vi städar om det utan kostnad' }
                ].map((item, idx) => (
                  <div key={idx} className="p-6 rounded-2xl" style={{ backgroundColor: '#fff', border: '1px solid #e8e4d9' }}>
                    <h4 className="font-bold mb-2" style={{ color: '#1a1a2e' }}>{item.q}</h4>
                    <p style={{ color: '#666' }}>{item.a}</p>
                  </div>
                ))}
              </div>

              <div className="mt-8 p-8 rounded-2xl text-center" style={{ backgroundColor: '#1a1a2e' }}>
                <h3 className="text-2xl font-bold mb-4 text-white">Föredrar Du Att Boka?</h3>
                <p className="mb-6" style={{ color: 'rgba(255,255,255,0.7)' }}>Använd vårt enkla bokningssystem.</p>
                <a href="/boka" className="inline-block px-8 py-3 rounded-full font-bold transition-all hover:scale-105"
                  style={{ backgroundColor: '#C9A84C', color: '#1a1a2e' }}>
                  Boka Online Nu
                </a>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;